import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { truncate } from 'utils/string-utils';
import { PATH_CLIENT_EDIT, PATH_CLIENT_DOSEMETER_EDIT } from 'routing/path'
import PageHeader from 'components/page-header'
import ConfirmActionModal from 'components/modals/confirm-action-modal'
import ClientDosemeterMeasurementSearchFilter from 'components/search-filters/client-dosemeter-measurement-search-filter'
import Table from 'components/table'
import { useGetMeasurementsQuery, useUpdateMeasurementStatusMutation, useMarkMeasurementAsResignedMutation, useImportMeasurementResultsMutation } from 'actions/client/client-dosemeter-measurement-api';
import { translationApi  } from 'actions/translation-api';
import { changeClient, changePage  } from 'store/client/slice.measurement-list-filter';
import { getDosemeterTypeDescription, getDosemeterTypeCode, DOSEMETER_TYPE_ENVIRONMENTAL, DOSEMETER_TYPE_PERSONAL, DOSEMETER_TYPE_PERSONAL_WRIST } from 'data/enums/dosemeter-type-enum';
import getMeasurementStatusDescription, { MEASUREMENT_STATUS_TO_SEND, MEASUREMENT_STATUS_IN_PROGRESS, MEASUREMENT_STATUS_DOSEMETER_LOST } from 'data/enums/measurement-status-enum';
import { showSuccessNotification, showErrorNotification } from "actions/action-complete-notificator";

const MeasurementList = () => {
	const dispatch = useDispatch();
	const params = useParams();
	const clientIdString = params.id;

	const measurementListFilter = useSelector(state => state.measurementListFilter);
	const { data: measurementList } = useGetMeasurementsQuery(measurementListFilter, { refetchOnMountOrArgChange: true });
	const [ updateMeasurementStatusAction ] = useUpdateMeasurementStatusMutation();
	const [ markMeasurementAsResignedAction ] = useMarkMeasurementAsResignedMutation();
	const [ importMeasurementResultsAction ] = useImportMeasurementResultsMutation();
	const translations = useSelector(translationApi.endpoints.getTranslations.select()).data ?? {};

	const changeMeasurementListPage = (filterChange) => {
        dispatch(changePage(filterChange.page));
	}

	const updateMeasurementStatus = async (measurement, newStatus) => {
		const result = await updateMeasurementStatusAction({ measurementId: measurement.measurementId, status: newStatus });
		if ('error' in result) {
			showErrorNotification(result.error);
		}
		else {
			showSuccessNotification(translations.statusChangedTo.replace('{0}', getMeasurementStatusDescription(newStatus, translations)));
		}
	}

	const deleteMeasurement = async (measurement) => {
		const result = await markMeasurementAsResignedAction(measurement.measurementId);
		if ('error' in result) {
			showErrorNotification(result.error);
		}
		else {
			showSuccessNotification(translations.measurementDeleted);
		}
	}

    const onFileSelected = async (event) => {
		const result = await importMeasurementResultsAction(event.target.files[0]);
		event.target.value = null;

		if ('error' in result) {
			showErrorNotification(result.error);
		}
		else {
			showSuccessNotification(translations.importResultsCompleted);
		}
	};

	const getPageHeader = () => {
		let title = translations.measurementList;
		const breadcrumbsCurrentPage = translations.measurementList;
		const buttons = [<div key="importResults"><input id="importResults" type="file" accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" onChange={async (event) => { await onFileSelected(event) }} hidden/><label htmlFor="importResults" className="btn btn-primary me-2 mb-0">{translations.importResults}</label></div>];
		let breadcrumbs = [];
		if (clientIdString && measurementList?.clientName) {
			title = `${measurementList.clientName} - ${translations.measurementList}`;
			breadcrumbs = [
				{ name: `${translations.client} ${measurementList.clientName}`, link: PATH_CLIENT_EDIT + clientIdString }
			]
		}

		return <PageHeader title={title} buttons={buttons} breadcrumbs={breadcrumbs} breadcrumbsCurrentPage={breadcrumbsCurrentPage} />
	}

	const pageHeader = getPageHeader();
	const headers = [`${translations.dosemeterNumberShort} (daw. ${translations.dosemeterNumberShort})`, translations.client, translations.user, translations.measurementPeriod, translations.measurementResult, translations.status, translations.actions];
	let rows = [];
	if (measurementList && measurementList.measurements) {
		rows = measurementList.measurements.map(function (measurement) {
			const measurementClientIdString = measurement.clientId ? measurement.clientId.toString().padStart(4, '0') : "";
			let dosemeterUserName;
			switch(measurement.dosemeterType) {
				case DOSEMETER_TYPE_ENVIRONMENTAL: dosemeterUserName = translations.dosemeterTypeEnvironmental; break;
				case DOSEMETER_TYPE_PERSONAL: dosemeterUserName = `${measurement.dosemeterUserFirstName} ${measurement.dosemeterUserLastName}`; break;
				case DOSEMETER_TYPE_PERSONAL_WRIST: dosemeterUserName = `${measurement.dosemeterUserFirstName} ${measurement.dosemeterUserLastName} (${translations.wrist?.toLowerCase()})`; break;
			}

			return {
				className: measurement.measurementWithoutResult ? "red-background" : measurement.dosemeterToSend ? "green-background" : "",
				link: PATH_CLIENT_DOSEMETER_EDIT + measurement.dosemeterId,
				columns: [
					`${measurementClientIdString}-${getDosemeterTypeCode(measurement.dosemeterType)}${measurement.dosemeterNumber}` + (measurement.dosemeterOldNumber ? ` (daw. ${measurementClientIdString}-${measurement.dosemeterOldNumber})` : ""),
					truncate(measurement.clientName, 30),
					truncate(dosemeterUserName, 50),
					`${measurement.periodStart.slice(0,10)} - ${measurement.periodEnd.slice(0,10)}`,
					measurement.resultAsString,
					getMeasurementStatusDescription(measurement.status, translations)
				],
				tooltips: [null, measurement.clientName, dosemeterUserName, null, null],
				buttons: measurement.status === MEASUREMENT_STATUS_TO_SEND ? [
					<div>
						<button type="button" key={"markMeasurementDosemeterAsSent" + measurement.measurementId} onClick={async () => {await updateMeasurementStatus(measurement, MEASUREMENT_STATUS_IN_PROGRESS)}} className="btn btn-sm btn-primary p-1 me-2" >{translations.actionSend}</button>
						<button type="button" key={"markMeasurementAsResigned" + measurement.measurementId} className="btn btn-sm btn-warning p-1 me-2" data-bs-toggle="modal" data-bs-target={"#confirm-resign-modal" + measurement.measurementId} >{translations.actionResign}</button>
						<ConfirmActionModal id={"confirm-resign-modal" + measurement.measurementId}
							bodyText={translations.confirmationDeleteMeasurement?.replace("{0}", `${measurementClientIdString}-${getDosemeterTypeCode(measurement.dosemeterType)}${measurement.dosemeterNumber}`)}
							confirmText={translations.yes} cancelText={translations.no}
							onClick={async () => {await deleteMeasurement(measurement)}}
						/>
					</div>
				] : measurement.status === MEASUREMENT_STATUS_IN_PROGRESS ? [
					<div>
						<button type="button" key={"markMeasurementAsLost" + measurement.measurementId} className="btn btn-sm btn-warning p-1 me-2" data-bs-toggle="modal" data-bs-target={"#confirm-lost-modal" + measurement.measurementId}>{translations.actionDosemeterLost}</button>
						<ConfirmActionModal id={"confirm-lost-modal" + measurement.measurementId}
							bodyText={translations.confirmationLostMeasurementDosemeter?.replace("{0}", `${measurementClientIdString}-${getDosemeterTypeCode(measurement.dosemeterType)}${measurement.dosemeterNumber}`)}
							confirmText={translations.yes} cancelText={translations.no}
							onClick={async () => {await updateMeasurementStatus(measurement, MEASUREMENT_STATUS_DOSEMETER_LOST)}}
						/>
					</div>
				] : []
			}
		});
	}

	return (
		<div>
			{pageHeader}
			<ClientDosemeterMeasurementSearchFilter />
			<Table fetchList={changeMeasurementListPage} headers={headers} rows={rows} pagination={measurementList?.pagination} translations={translations} />
		</div>
	);
}

export default MeasurementList;
